import { useLang } from 'hooks/useLang';
import React, { useRef, useState, useEffect } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

interface HorizontalScrollerProps {
    children: React.ReactNode;
}

const MySwiper: React.FC<HorizontalScrollerProps> = ({ children }) => {
    const { lang } = useLang();
    const isRTL = lang === "ar";
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(false);
    const [canScroll, setCanScroll] = useState(false);

    // Dragging state
    const isDragging = useRef(false);
    const startX = useRef(0);
    const scrollLeft = useRef(0);

    // Handle scroll events
    const handleScroll = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            const maxScrollLeft = scrollWidth - clientWidth;

            if (isRTL) {
                setShowLeftArrow(scrollLeft < 0);
                setShowRightArrow(Math.abs(scrollLeft) < maxScrollLeft - 1);
            } else {
                setShowLeftArrow(scrollLeft > 0);
                setShowRightArrow(scrollLeft < maxScrollLeft);
            }
        }
    };

    // Check for overflow on mount and when children change
    const checkOverflow = () => {
        if (scrollContainerRef.current) {
            const { scrollWidth, clientWidth } = scrollContainerRef.current;
            setCanScroll(scrollWidth > clientWidth);
        }
    };

    useEffect(() => {
        checkOverflow();
        handleScroll();
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [children, isRTL]);

    // Mouse event handlers for drag scrolling
    const handleMouseDown = (e: React.MouseEvent) => {
        if (!scrollContainerRef.current) return;

        isDragging.current = true;
        startX.current = e.pageX - scrollContainerRef.current.offsetLeft;
        scrollLeft.current = scrollContainerRef.current.scrollLeft;

        scrollContainerRef.current.style.cursor = 'grabbing';
        scrollContainerRef.current.style.scrollBehavior = 'auto'; // Disable smooth scrolling during drag
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging.current || !scrollContainerRef.current) return;

        e.preventDefault();
        const x = e.pageX - scrollContainerRef.current.offsetLeft;
        const walk = (x - startX.current) * (isRTL ? -1 : 1); // Adjust direction for RTL
        scrollContainerRef.current.scrollLeft = scrollLeft.current - walk;
        handleScroll();
    };

    const handleMouseUp = () => {
        isDragging.current = false;
        if (scrollContainerRef.current) {
            scrollContainerRef.current.style.cursor = 'grab';
            scrollContainerRef.current.style.scrollBehavior = 'smooth'; // Re-enable smooth scrolling
        }
    };

    // Handle mouse wheel scrolling - modified to allow vertical scrolling
    const handleWheel = (e: WheelEvent) => {
        if (!scrollContainerRef.current) return;

        // Only handle horizontal scroll if shift key is pressed or if the event is primarily horizontal
        if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
            e.preventDefault();
            scrollContainerRef.current.scrollBy({
                left: e.deltaY * (isRTL ? -1 : 1),
                behavior: 'smooth'
            });
        }
        // Otherwise, let the vertical scroll happen naturally
    };

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        container.addEventListener('mousemove', handleMouseMove);
        container.addEventListener('mouseup', handleMouseUp);
        container.addEventListener('mouseleave', handleMouseUp);
        container.addEventListener('wheel', handleWheel, { passive: false });

        return () => {
            container.removeEventListener('mousemove', handleMouseMove);
            container.removeEventListener('mouseup', handleMouseUp);
            container.removeEventListener('mouseleave', handleMouseUp);
            container.removeEventListener('wheel', handleWheel);
        };
    }, []);

    return (
        <div className="flex items-center relative select-none">


            <div
                className="w-full h-full transition-all duration-300"
                style={{
                    maskImage: `linear-gradient(to ${isRTL ? 'left' : 'right'}, 
                        transparent 0%, 
                        black ${showLeftArrow ? '9%' : '0%'}, 
                        black ${showRightArrow ? '88%' : '100%'}, 
                        transparent 100%)`,
                    WebkitMaskImage: `linear-gradient(to ${isRTL ? 'left' : 'right'}, 
                        transparent 0%, 
                        black ${showLeftArrow ? '9%' : '0%'}, 
                        black ${showRightArrow ? '88%' : '100%'}, 
                        transparent 100%)`,
                }}
            >
                <div
                    ref={scrollContainerRef}
                    className="flex overflow-x-auto scroll-smooth gap-4 p-2 w-full hide-scrollbar cursor-grab"
                    onScroll={handleScroll}
                    onMouseDown={handleMouseDown}
                    dir={isRTL ? 'rtl' : 'ltr'}
                >
                    {children}
                </div>
            </div>

        </div>
    );
};

export default MySwiper;

// Hide scrollbar styles
const styles = `
    .hide-scrollbar {
        scrollbar-width: none;
        -ms-overflow-style: none;
    }
    .hide-scrollbar::-webkit-scrollbar {
        display: none;
    }
`;

// Inject styles into the document head
if (typeof document !== 'undefined') {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
}